import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useExitBlocker } from '@/hooks/use-exit-blocker';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  MapPin,
  Play,
  CheckCircle2,
  ArrowLeft,
  FileText,
  Loader2,
  Navigation,
  Package,
  QrCode,
  X,
  Fingerprint,
  Languages,
} from 'lucide-react';
import { SOPSection } from './SOPSection';
import { cn } from '@/lib/utils';
import { translateText } from '@/lib/translate';
import { SectionCard } from './SectionCard';
import { ValidationLoader, getCheckInSteps, getCheckOutSteps } from './ValidationLoader';
import { PreviewBanner } from './PreviewBanner';
import { InventoryOrderSection } from './InventoryOrderSection';
import { SnowSessionSection } from './SnowSessionSection';
import { ExtraPaidSessionSection } from './ExtraPaidSessionSection';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { isPlainObject } from '@/lib/checklistJson';
import { useRole } from '@/contexts/RoleContext';
import { Html5Qrcode } from 'html5-qrcode';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  parsePropertyDetailTabParam,
  propertyDetailTabToSearchParam,
  type PropertyDetailTab,
} from '@/lib/sectionDetailTabParams';
import {
  CLEANER_PROPERTIES_QUERY_KEY,
  CLEANER_SECTIONS_QUERY_KEY,
  CLEANER_TASKS_QUERY_KEY,
} from '@/lib/cleanerQueryKeys';
import {
  countRemainingPropertyTasks,
  findCheckoutTaskForLocation,
  findLocationTaskForWorkSession,
  formatCheckOutNotes,
  hasActiveWorkSession,
  setActiveWorkSession,
  shouldWriteCheckInMetadata,
  type WorkSessionTask,
} from '@/lib/cleanerWorkSession';
import { cleanerActiveTasksOrFilter } from '@/lib/cleanerTaskQuery';
import { closeWorkSession, fetchOpenWorkSessions, startWorkSession } from '@/lib/workSessions';

interface ChecklistTask {
  id: string;
  name: string;
  completed: boolean;
  note?: string;
  type: 'recurring' | 'adhoc';
  requiresPhoto?: boolean;
  photoTaken?: boolean;
}

interface InventoryItem {
  id: string;
  name: string;
  unit: string;
  ordered?: boolean;
}

interface ActivityLogEntry {
  id: string;
  person: string;
  action: string;
  timestamp: string;
}

interface Section {
  id: string;
  name: string;
  assignedTo?: string;
  isAssignedToMe: boolean;
  allowCrossAccess: boolean;
  tasksTotal: number;
  tasksCompleted: number;
  checklistTasks: ChecklistTask[];
  inventory: InventoryItem[];
  activityLog: ActivityLogEntry[];
  instructionNotes?: string;
  accessCode?: string;
  taskId?: string; // ID głównego zadania dla sekcji (cleaning_tasks.id)
}

export interface PropertyDetail {
  id: string;
  address: string;
  clientName?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed';
  sections: Section[];
  inventory: InventoryItem[];
  coordinatorNotes?: string;
  qr_code_token?: string | null;
  location_master_id?: string | null;
  checklist?: unknown;
  validation_config?: {
    qr_required?: boolean;
    gps_required?: boolean;
    biometric_required?: boolean;
  } | null;
  latitude?: number | null;
  longitude?: number | null;
}

interface PropertyDetailSheetProps {
  property: PropertyDetail | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  embedded?: boolean;
  embeddedClassName?: string;
  onWorkStarted?: () => void;
}

const getPriorityConfig = (t: (key: string) => string) => ({
  low: { label: t('priority.low'), className: 'bg-slate-100 text-slate-600 dark:bg-zinc-800 dark:text-zinc-400' },
  medium: { label: t('priority.medium'), className: 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400' },
  high: { label: t('priority.high'), className: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400' },
  urgent: { label: t('priority.urgent'), className: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400' },
});

export const PropertyDetailSheet = ({
  property,
  open = true,
  onOpenChange,
  embedded = false,
  embeddedClassName,
  onWorkStarted
}: PropertyDetailSheetProps) => {
  const { t, i18n } = useTranslation();
  const { currentOrgId, isSnowSeason } = useRole();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const propertyTab = parsePropertyDetailTabParam(searchParams.get('tab'));
  const [isValidating, setIsValidating] = useState(false);
  const [validationType, setValidationType] = useState<'checkin' | 'checkout' | null>(null);
  const [validationStep, setValidationStep] = useState(0);
  const [isWorkStarted, setIsWorkStarted] = useState(false);
  const [isVerifyingLocation, setIsVerifyingLocation] = useState(false);
  const [isRefreshingGPS, setIsRefreshingGPS] = useState(false);
  const [gpsBypassEnabled, setGpsBypassEnabled] = useState(false);
  const [gpsStatus, setGpsStatus] = useState<{
    distance?: number;
    radius?: number;
    isWithinRange?: boolean;
    isTracking?: boolean;
  } | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scannedCode, setScannedCode] = useState<string | null>(null);
  const [isQRCodeValidated, setIsQRCodeValidated] = useState(false);
  const [qrScannerError, setQrScannerError] = useState<string | null>(null);
  const [isBiometricValidated, setIsBiometricValidated] = useState(false);
  const [showValidationList, setShowValidationList] = useState(false);
  const [showIncompleteConfirm, setShowIncompleteConfirm] = useState(false);
  const [activeTaskIds, setActiveTaskIds] = useState<string[]>([]); // Tablica faktycznych task_id dla property.id (location_id) - wszystkie aktywne zadania
  const [sopTranslatedText, setSopTranslatedText] = useState<string | null>(null);
  const [isSopTranslating, setIsSopTranslating] = useState(false);
  const qrCodeScannerRef = useRef<Html5Qrcode | null>(null);
  const qrCodeScannerContainerRef = useRef<HTMLDivElement | null>(null);
  const isInitializingRef = useRef<boolean>(false);
  const isStoppingRef = useRef<boolean>(false);

  // Validation requirements configuration - dynamiczne z validation_config
  // Parsowanie konfiguracji z domyślnymi wartościami: GPS=true, QR=false
  const validationConfig = property?.validation_config as {
    qr_required?: boolean;
    gps_required?: boolean;
    biometric_required?: boolean;
  } | null | undefined;
  
  // Domyślne wartości: GPS=true, QR=false (zgodnie z wymaganiami)
  const needsGps = validationConfig?.gps_required ?? true;
  const needsQr = validationConfig?.qr_required ?? false;
  const needsBiometric = validationConfig?.biometric_required ?? false;

  // Blokada wyjścia z aplikacji podczas pracy
  useExitBlocker(isWorkStarted);

  // Funkcja do znajdowania task_id dla property.id (location_id) - wyciągnięta do osobnej funkcji
  // aby można było ją wywołać zarówno z useEffect, jak i z callbacku onTaskUpdated
  const findTaskId = useCallback(async () => {
    if (!property || !currentOrgId) {
      return;
    }

    try {
      // Oblicz datę początku i końca dzisiaj (00:00:00 - 23:59:59) dla filtrowania zadań
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayStartISO = todayStart.toISOString();
      
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      const todayEndISO = todayEnd.toISOString();
      
      // INCLUSIVE FETCHING: Pobierz wszystkie aktywne zadania w jednym zapytaniu:
      // - Wszystkie pending i in_progress (niezależnie od typu)
      // - ORAZ wszystkie done z dzisiaj (completed_at >= dzisiaj 00:00:00) LUB done bez completed_at (nowe zadania)
      // - ORAZ wszystkie zadania jednorazowe (coordinator_single), które nie są ukończone (status != 'done')
      // - ORAZ wszystkie zadania dodatkowe (employee_extra), które nie są ukończone (status != 'done')
      // - ORAZ wszystkie zadania standardowe SOP (sop_standard) z dzisiejszą datą (scheduled_at = dzisiaj)
      // - ORAZ wszystkie zadania długoterminowe (long_term) z scheduled_at <= DZISIAJ i status != 'done'
      // Dzięki temu zadania jednorazowe i dodatkowe są widoczne tak długo, aż nie zostaną ukończone
      const { data: activeTasksData, error: activeTaskError } = await supabase
        .from('cleaning_tasks')
        .select('id, status, completed_at, created_at, task_type, scheduled_at')
        .eq('location_id', property.id)
        .eq('org_id', currentOrgId)
        .neq('status', 'cancelled')
        .or(cleanerActiveTasksOrFilter(todayStartISO, todayEndISO))
        .order('created_at', { ascending: false });

      if (activeTaskError) {
        console.error('[PropertyDetailSheet] Błąd pobierania aktywnych task_id:', activeTaskError);
        // NIE ustawiaj na pustą tablicę w przypadku błędu - zachowaj obecną wartość
        return;
      }

      // Aktualizuj stan tylko gdy otrzymano poprawny wynik z bazy
      // Jeśli activeTasksData zawiera zadania, zapisz wszystkie ID
      if (activeTasksData && activeTasksData.length > 0) {
        const taskIds = activeTasksData.map(t => t.id);
        // OCHRONA: Nie nadpisuj istniejących taskIds pustą tablicą podczas przejściowych problemów
        // Zachowaj poprzednią wartość jeśli nowa tablica jest pusta, a poprzednia nie była
        setActiveTaskIds(prev => {
          // Jeśli poprzednia tablica była niepusta, a nowa jest pusta, zachowaj poprzednią
          if (prev.length > 0 && taskIds.length === 0) {
            console.warn('[PropertyDetailSheet] Otrzymano pustą listę taskIds, zachowuję poprzednią wartość:', prev);
            return prev;
          }
          return taskIds;
        });
        const pendingCount = activeTasksData.filter(t => t.status === 'pending').length;
        const inProgressCount = activeTasksData.filter(t => t.status === 'in_progress').length;
        const doneTodayCount = activeTasksData.filter(t => t.status === 'done').length;
        console.log(`[PropertyDetailSheet] Znaleziono ${activeTasksData.length} aktywnych zadań (pending: ${pendingCount}, in_progress: ${inProgressCount}, done dzisiaj: ${doneTodayCount}):`, taskIds);
      } else if (activeTasksData && activeTasksData.length === 0) {
        // Jeśli otrzymano pustą tablicę, ale to może być przejściowy problem - nie czyść od razu
        console.log('[PropertyDetailSheet] Otrzymano pustą listę zadań - możliwe przejściowe problemy z synchronizacją');
      }
      // Jeśli brak taskData (błąd), nie modyfikuj activeTaskIds - zachowaj obecną wartość
    } catch (error) {
      console.error('[PropertyDetailSheet] Błąd podczas wyszukiwania task_id:', error);
      // NIE ustawiaj na null w przypadku błędu - zachowaj obecną wartość
    }
  }, [property, currentOrgId]);

  // Znajdź task_id dla property.id (location_id) - wywołanie przy montowaniu i zmianie zależności
  useEffect(() => {
    // NIE ustawiaj propertyTaskId na null, jeśli property.id się nie zmienia
    // Stabilizacja stanu: aktualizuj tylko gdy otrzymasz poprawny wynik z bazy
    if (!property || !currentOrgId || !open) {
      // Ustaw na pustą tablicę tylko gdy sheet jest zamknięty lub brak property
      if (!open || !property) {
        setActiveTaskIds([]);
      }
      return;
    }

    findTaskId();
  }, [property?.id, currentOrgId, open, findTaskId]);

  // Sesja pracy to wyłącznie Check-in w tej przeglądarce. Status budynku/zadań nie odblokowuje listy
  // — dzięki temu można logować się kilka razy dziennie i realizować zadania na raty.
  useEffect(() => {
    if (!property || !currentOrgId || !open) {
      setIsWorkStarted(false);
      return;
    }

    const hasLocalSession = hasActiveWorkSession(property.id);
    setIsWorkStarted(hasLocalSession);

    if (hasLocalSession) {
      console.log('[PropertyDetailSheet] Wykryto sesję Check-in dla property:', property.id);
    } else {
      console.log('[PropertyDetailSheet] Brak sesji - isWorkStarted false dla property:', property.id);
    }

    const onSessionChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ locationId?: string; active?: boolean }>).detail;
      if (!detail?.locationId || detail.locationId !== property.id) return;
      setIsWorkStarted(detail.active === true);
    };
    window.addEventListener("domio-work-session-changed", onSessionChanged);
    return () => window.removeEventListener("domio-work-session-changed", onSessionChanged);
  }, [property?.id, currentOrgId, open]);

  // Resetuj gpsStatus gdy property się zmienia lub sheet się zamyka
  useEffect(() => {
    if (!open || !property) {
      // Resetuj stan GPS gdy sheet się zamyka lub property się zmienia
      setGpsStatus(null);
      setIsScanning(false);
      setScannedCode(null);
      setIsQRCodeValidated(false);
      setQrScannerError(null);
      setIsBiometricValidated(false);
      setShowValidationList(false);
    }
  }, [open, property?.id]);

  // Reset tłumaczenia SOP przy zmianie property lub zamknięciu sheet
  useEffect(() => {
    setSopTranslatedText(null);
  }, [property?.id, open]);

  // --- DIAGNOSTYKA LICZNIKÓW: log wartości sekcji przy otwarciu sheet (PropertyDetailSheet) ---
  useEffect(() => {
    if (!open || !property?.sections?.length) return;
    property.sections.forEach((s) => {
      const tasksRemaining = s.tasksTotal - s.tasksCompleted;
      console.log('[PropertyDetailSheet] DIAGNOSTYKA SEKCJI (wyświetlane)', {
        nazwaSekcji: s.name,
        sectionId: s.id,
        propertyAddress: property.address,
        tasksTotal: s.tasksTotal,
        tasksCompleted: s.tasksCompleted,
        tasksRemaining,
      });
    });
  }, [open, property?.id, property?.address, property?.sections]);

  // Helper function to safely stop and clear scanner
  const safelyStopScanner = async (scanner: Html5Qrcode | null): Promise<void> => {
    if (!scanner || isStoppingRef.current) {
      return;
    }

    isStoppingRef.current = true;
    try {
      // Check if scanner is running before stopping
      // html5-qrcode doesn't expose getState() directly, so we use try-catch
      await scanner.stop().catch((err: Error) => {
        // Ignore "cannot clear while scan is ongoing" and "cannot transition" errors
        if (
          err.message.includes('cannot clear while scan is ongoing') ||
          err.message.includes('cannot transition') ||
          err.message.includes('close is first')
        ) {
          console.warn('[PropertyDetailSheet] Skaner już w trakcie zatrzymywania, ignoruję błąd:', err.message);
          return;
        }
        throw err;
      });
    } catch (err) {
      console.error('[PropertyDetailSheet] Błąd podczas zatrzymywania skanera:', err);
    } finally {
      try {
        scanner.clear();
      } catch (clearErr: unknown) {
        // Ignoruj błędy "cannot clear while scan is ongoing" - to normalne podczas zatrzymywania
        const errorMessage = clearErr instanceof Error ? clearErr.message : String(clearErr);
        if (
          errorMessage.includes('cannot clear while scan is ongoing') ||
          errorMessage.includes('cannot transition') ||
          errorMessage.includes('close is first') ||
          errorMessage.includes('Scanner is not running')
        ) {
          console.warn('[PropertyDetailSheet] Skaner już w trakcie czyszczenia, ignoruję błąd:', errorMessage);
        } else {
          console.error('[PropertyDetailSheet] Błąd podczas czyszczenia skanera:', clearErr);
        }
      }
      isStoppingRef.current = false;
    }
  };

  // Pancerna walidacja QR - useCallback dla stabilności referencji
  const handleScanSuccess = useCallback(async (decodedText: string) => {
    if (!property || isQRCodeValidated) return;

    // Ekstrakcja tokenu: jeśli zeskanowany tekst jest linkiem, bierzemy tylko to co po ostatnim ukośniku
    const scannedValue = decodedText.includes('/') 
      ? decodedText.split('/').pop() || "" 
      : decodedText;
    
    const scannedClean = String(scannedValue).trim().toLowerCase();

    // Budujemy listę dopuszczalnych kodów jako proste stringi (znormalizowane do lowercase i trim)
    const validOptions = [
      String(property.id || ''),
      String(property.qr_code_token || ''),
      String(property.location_master_id || '')
    ].map(t => t.trim().toLowerCase()).filter(t => t.length > 0);

    const isValid = validOptions.includes(scannedClean);

    if (isValid) {
      // 1. Natychmiast ustawiamy stan, by zablokować kolejne skany
      setIsQRCodeValidated(true);
      
      // 2. Ważne: najpierw stopujemy skaner, potem robimy toast/redirect
      // To zapobiega błędom "Cannot transition" i "cannot clear while scan is ongoing"
      if (qrCodeScannerRef.current) {
        try {
          await qrCodeScannerRef.current.stop()
            .then(() => {
              // Po zatrzymaniu, wyczyść skaner
              if (qrCodeScannerRef.current) {
                qrCodeScannerRef.current.clear();
                qrCodeScannerRef.current = null;
              }
            })
            .catch((e) => {
              console.warn("Cleanup error:", e);
              // Nawet jeśli stop się nie udał, spróbuj wyczyścić
              try {
                if (qrCodeScannerRef.current) {
                  qrCodeScannerRef.current.clear();
                }
              } catch (clearErr) {
                console.warn("Clear error:", clearErr);
              }
              qrCodeScannerRef.current = null;
            });
        } catch (e) {
          console.warn("Cicha obsługa błędu skanera:", e);
          qrCodeScannerRef.current = null;
        }
      }

      setIsScanning(false);
      isInitializingRef.current = false;
      
      // 3. Dopiero teraz pokazujemy toast (po zatrzymaniu skanera)
      toast.success("Zalogowano pomyślnie");
    } else {
      // Wyświetlamy błąd tylko raz na 2 sekundy, żeby nie spamować
      toast.error("Nieprawidłowy kod QR", { id: 'qr-error' });
      setScannedCode(null);
    }
  }, [property, isQRCodeValidated]);

  // Inicjalizacja i czyszczenie skanera QR
  useEffect(() => {
    let isMounted = true;

    // Cleanup: Zatrzymaj skaner jeśli isScanning jest false lub property nie istnieje
    if (!isScanning || !property || isQRCodeValidated) {
      if (qrCodeScannerRef.current && !isInitializingRef.current) {
        safelyStopScanner(qrCodeScannerRef.current).then(() => {
          if (isMounted) {
            qrCodeScannerRef.current = null;
          }
        });
      }
      // Resetuj ref kontenera gdy skanowanie jest wyłączone
      if (qrCodeScannerContainerRef.current) {
        qrCodeScannerContainerRef.current = null;
      }
      isInitializingRef.current = false;
      return;
    }

    // Zapobiegaj podwójnemu uruchomieniu
    if (isInitializingRef.current) {
      console.warn('[PropertyDetailSheet] Skaner już w trakcie inicjalizacji, pomijam...');
      return;
    }

    // Inicjalizuj skaner gdy isScanning zmienia się na true
    const initQRScanner = async () => {
      // Upewnij się, że poprzedni skaner jest zatrzymany przed uruchomieniem nowego
      if (qrCodeScannerRef.current) {
        await safelyStopScanner(qrCodeScannerRef.current);
        if (isMounted) {
          qrCodeScannerRef.current = null;
        }
      }

      isInitializingRef.current = true;

      // Czekamy aż DOM będzie gotowy (zwiększony timeout dla stabilności)
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Sprawdź czy komponent nadal jest zamontowany i chce skanować
      if (!isMounted || !isScanning || !property || isQRCodeValidated) {
        isInitializingRef.current = false;
        return;
      }

      // Użyj unikalnego ID opartego na property.id, aby uniknąć konfliktów
      const containerId = `qr-reader-${property.id}`;
      
      // Sprawdź czy kontener istnieje w DOM
      const readerElement = document.getElementById(containerId);
      if (!readerElement && !qrCodeScannerContainerRef.current) {
        console.error('[PropertyDetailSheet] Brak kontenera dla skanera QR w DOM');
        setIsScanning(false);
        isInitializingRef.current = false;
        return;
      }

      // Ustaw ID kontenera jeśli jeszcze nie ma
      if (qrCodeScannerContainerRef.current && !qrCodeScannerContainerRef.current.id) {
        qrCodeScannerContainerRef.current.id = containerId;
      }
      
      try {
        const qrCodeScanner = new Html5Qrcode(containerId);
        if (!isMounted) {
          // Komponent został odmontowany podczas inicjalizacji
          await safelyStopScanner(qrCodeScanner);
          return;
        }
        
        qrCodeScannerRef.current = qrCodeScanner;

        // Konfiguracja skanera
        const config = {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        };

        // Uruchom skaner z kamerą - użyj handleScanSuccess jako callback
        await qrCodeScanner.start(
          { facingMode: 'environment' }, // Użyj tylnej kamery na telefonie
          config,
          (decodedText, decodedResult) => {
            // Wywołaj pancerną funkcję walidacji
            handleScanSuccess(decodedText).catch((err) => {
              console.error('[PropertyDetailSheet] Błąd w handleScanSuccess:', err);
            });
          },
          (errorMessage) => {
            // Ignoruj błędy parsowania (są normalne podczas skanowania)
            // Loguj tylko poważne błędy
            if (!errorMessage.includes('NotFoundException')) {
              // Nie loguj NotFoundException - to normalne podczas skanowania
            }
          }
        );

        if (isMounted) {
          isInitializingRef.current = false;
          setQrScannerError(null);
          console.log('[PropertyDetailSheet] Skaner QR uruchomiony pomyślnie');
        }
      } catch (error) {
        isInitializingRef.current = false;
        console.error('[PropertyDetailSheet] Błąd inicjalizacji skanera QR:', error);
        const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
        
        if (isMounted) {
          setQrScannerError(errorMessage);
          
          // Cleanup w przypadku błędu
          if (qrCodeScannerRef.current) {
            safelyStopScanner(qrCodeScannerRef.current).then(() => {
              if (isMounted) {
                qrCodeScannerRef.current = null;
              }
            });
          }
          
          if (errorMessage.includes('Permission denied') || errorMessage.includes('NotAllowedError')) {
            toast.error('Brak uprawnień do kamery. Sprawdź ustawienia przeglądarki.');
          } else if (errorMessage.includes('NotFoundError') || errorMessage.includes('DevicesNotFoundError')) {
            toast.error('Nie znaleziono kamery. Upewnij się, że urządzenie ma dostęp do kamery.');
          } else {
            toast.error(`Błąd skanera: ${errorMessage}`);
          }
          
          setIsScanning(false);
        }
      }
    };

    if (isScanning && !isQRCodeValidated) {
      initQRScanner();
    }

    // Cleanup przy unmount lub zmianie isScanning
    return () => {
      isMounted = false;
      if (qrCodeScannerRef.current && !isInitializingRef.current) {
        safelyStopScanner(qrCodeScannerRef.current).then(() => {
          qrCodeScannerRef.current = null;
        });
      }
      // Resetuj ref kontenera w cleanup
      if (qrCodeScannerContainerRef.current) {
        qrCodeScannerContainerRef.current = null;
      }
      isInitializingRef.current = false;
    };
  }, [isScanning, property?.id, property?.qr_code_token, property?.location_master_id, isQRCodeValidated, handleScanSuccess]);

  // Ochrona przed zamknięciem karty/odświeżeniem gdy sheet jest otwarty
  useEffect(() => {
    if (!open) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // Wymagane przez Chrome/Edge
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [open]);

  // GPS state reset only. Real GPS checks are triggered by explicit user actions.
  useEffect(() => {
    if (!open || !property || !currentOrgId) {
      setGpsStatus(null);
    };
  }, [open, property?.id, currentOrgId]);

  // Funkcja bezpiecznego wyjścia z potwierdzeniem
  const closeView = useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleSafeExit = () => {
    console.log('[PropertyDetailSheet] handleSafeExit wywołane');
    const confirmed = window.confirm(t('property.exitConfirm'));
    if (confirmed) {
      closeView();
    }
  };

  if (!property) return null;

  const priorityConfig = getPriorityConfig(t);
  const priority = priorityConfig[property.priority];
  const totalTasks = property.sections.reduce((sum, s) => sum + s.tasksTotal, 0);
  const completedTasks = property.sections.reduce((sum, s) => sum + s.tasksCompleted, 0);

  // Check if all required validations are satisfied
  const isGpsValid = gpsBypassEnabled || gpsStatus?.isWithinRange === true;
  const isQrValid = isQRCodeValidated === true;
  const isBiometricValid = isBiometricValidated === true;
  
  const isAllRequirementsMet = () => {
    // Sprawdź tylko te wymagania, które są włączone w konfiguracji
    const gpsOk = !needsGps || isGpsValid;
    const qrOk = !needsQr || isQrValid;
    const biometricOk = !needsBiometric || isBiometricValid;
    return gpsOk && qrOk && biometricOk;
  };

  const runValidation = async (type: 'checkin' | 'checkout') => {
    setIsValidating(true);
    setValidationType(type);
    setValidationStep(0);

    // Simulate step-by-step validation
    for (let i = 0; i <= 3; i++) {
      await new Promise(resolve => setTimeout(resolve, 800));
      setValidationStep(i);
    }

    await new Promise(resolve => setTimeout(resolve, 500));
    setIsValidating(false);
    setValidationType(null);

    if (type === 'checkin') {
      setIsWorkStarted(true);
      if (property) {
        setActiveWorkSession(property.id, true);
      }
      toast.success(t('property.workStarted'));
    } else {
      toast.success(t('property.workEnded'));
      closeView();
    }
  };


  const handleStartWork = async () => {
    if (!property || !currentOrgId) {
      console.error('[PropertyDetailSheet] handleStartWork - brak property lub currentOrgId:', { property: !!property, currentOrgId });
      toast.error(t('errors.unexpectedError'));
      return;
    }

    setIsVerifyingLocation(true);

    try {
      console.log('[PropertyDetailSheet] handleStartWork - rozpoczęcie dla property:', property.id, 'org_id:', currentOrgId);
      // Check if running on localhost or development mode
      const isDevelopment = process.env.NODE_ENV === 'development' ||
                           window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname === '';

      // Step 1: Get GPS location using navigator.geolocation.getCurrentPosition
      // Skip GPS verification if bypass is enabled OR in development mode OR GPS is not required
      let userLatitude: number | null = null;
      let userLongitude: number | null = null;
      let position: GeolocationPosition | null = null;

      const shouldSkipGPS = isDevelopment || gpsBypassEnabled || !needsGps;

      if (shouldSkipGPS) {
        if (!needsGps) {
          console.log('[PropertyDetailSheet] GPS nie jest wymagany w konfiguracji - pomijanie weryfikacji GPS');
        } else {
          console.log('[PropertyDetailSheet] Tryb deweloperski lub bypass GPS - pomijanie weryfikacji GPS');
        }
        // Use mock coordinates for development/bypass or when GPS is not required
        userLatitude = 52.2297;
        userLongitude = 21.0122;
      } else {
        position = await new Promise<GeolocationPosition>((resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error('Geolocation not supported'));
            return;
          }

          navigator.geolocation.getCurrentPosition(
            resolve,
            (error) => {
              if (error.code === error.PERMISSION_DENIED) {
                reject(new Error('PERMISSION_DENIED'));
              } else if (error.code === error.POSITION_UNAVAILABLE) {
                reject(new Error('POSITION_UNAVAILABLE'));
              } else if (error.code === error.TIMEOUT) {
                reject(new Error('TIMEOUT'));
              } else {
                reject(error);
              }
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            }
          );
        });

        userLatitude = position.coords.latitude;
        userLongitude = position.coords.longitude;

        console.log('[PropertyDetailSheet] GPS - Współrzędne użytkownika:', {
          latitude: userLatitude,
          longitude: userLongitude,
          accuracy: position.coords.accuracy,
        });
      }

      // Step 2: Validate location proximity via RPC (returns detailed JSON)
      // Skip RPC call if GPS bypass is enabled
      let proximityResult: {
        success?: boolean;
        is_within_range?: boolean;
        distance?: number;
        radius?: number;
        distance_meters?: number;
        geofence_radius_meters?: number;
        has_geo_location?: boolean;
        error?: string;
        location_coordinates?: { latitude: number; longitude: number };
        user_coordinates?: { latitude: number; longitude: number };
      } | null = null;

      if (!shouldSkipGPS) {
        console.log('[PropertyDetailSheet] ID Lokalizacji:', property.id);
        
        const empLat = position ? position.coords.latitude : userLatitude;
        const empLng = position ? position.coords.longitude : userLongitude;
        console.log('[PropertyDetailSheet] GPS debug - Współrzędne pracownika:', { latitude: empLat, longitude: empLng }, 'Współrzędne budynku (property.latitude/longitude):', property.latitude != null && property.longitude != null ? { latitude: property.latitude, longitude: property.longitude } : 'brak');
        if (position) {
          console.log('[PropertyDetailSheet] Wysyłam do bazy:', { lat: position.coords.latitude, lng: position.coords.longitude });
        } else {
          console.log('[PropertyDetailSheet] Wysyłam do bazy:', { lat: userLatitude, lng: userLongitude });
        }
        
        const { data: proximityData, error: proximityError } = await supabase.rpc(
          'check_location_proximity',
          {
            p_location_id: property.id,
            p_user_latitude: empLat,
            p_user_longitude: empLng,
          }
        );

        if (proximityError) {
          console.error('[PropertyDetailSheet] Błąd walidacji GPS:', proximityError);
          toast.error(t('errors.unexpectedError'));
          setIsVerifyingLocation(false);
          return;
        }

        // Log RAW response from API before parsing (critical for debugging)
        console.log('[PropertyDetailSheet] handleStartWork - RAW proximityData from API:', proximityData);
        console.log('[PropertyDetailSheet] handleStartWork - proximityData type:', typeof proximityData);
        console.log('[PropertyDetailSheet] handleStartWork - proximityData.distance:', isPlainObject(proximityData) ? proximityData.distance : undefined);
        console.log('[PropertyDetailSheet] handleStartWork - proximityData.radius:', isPlainObject(proximityData) ? proximityData.radius : undefined);
        console.log('[PropertyDetailSheet] handleStartWork - proximityData.is_within_range:', isPlainObject(proximityData) ? proximityData.is_within_range : undefined);

        // Parse JSON response from RPC function
        proximityResult = proximityData as typeof proximityResult;

        // CRITICAL: Serwer zwraca { distance, radius }, nie { distance_meters, geofence_radius_meters }
        const distance = proximityResult?.distance ?? proximityResult?.distance_meters;
        const radius = proximityResult?.radius ?? proximityResult?.geofence_radius_meters;
        const isWithinRange = proximityResult?.is_within_range ?? false;

        // Log target coordinates from database (RPC zwraca location_coordinates z latitude/longitude)
        const targetCoords = proximityResult?.location_coordinates;
        console.info('[PropertyDetailSheet] Budynek z bazy (RPC):', targetCoords || 'BRAK DANYCH - latitude/longitude nie istnieją w bazie dla tego obiektu');

        // Detailed GPS diagnostics logging
        console.log('[PropertyDetailSheet] GPS - Szczegółowa diagnostyka:', {
          'Współrzędne obiektu z bazy (latitude/longitude)': targetCoords || 'brak danych',
          'Współrzędne użytkownika': proximityResult?.user_coordinates || {
            latitude: userLatitude,
            longitude: userLongitude,
          },
          'Odległość': distance !== undefined && distance !== null
            ? `${distance.toFixed(2)} metrów`
            : 'brak danych',
          'Wymagany promień': radius !== undefined && radius !== null
            ? `${radius} metrów`
            : 'brak danych',
          'W zasięgu': isWithinRange ? 'TAK' : 'NIE',
          'latitude/longitude w bazie': proximityResult?.has_geo_location ? 'TAK' : 'NIE',
          'Sukces': proximityResult?.success ? 'TAK' : 'NIE',
        });

        // ZAUFAJ RPC: Jeśli przyszły dane, po prostu je pokaż - ZMUŚ Reacta do przerysowania
        // CRITICAL: Always update state if we have distance from API response
        if (proximityResult && typeof distance === 'number' && !isNaN(distance)) {
          // Zmuś Reacta do przerysowania - zawsze aktualizuj stan z danymi z API
          const newGpsStatus = {
            distance: distance,
            radius: typeof radius === 'number' ? radius : undefined,
            isWithinRange: isWithinRange,
            isTracking: true,
          };
          setGpsStatus(newGpsStatus);
          console.log('[PropertyDetailSheet] handleStartWork - Zaktualizowano stan z danymi z API:', {
            distance: newGpsStatus.distance,
            radius: newGpsStatus.radius,
            isWithinRange: newGpsStatus.isWithinRange,
            rawResponse: proximityResult,
          });
        } else {
          console.warn('[PropertyDetailSheet] handleStartWork - Brak poprawnych danych w odpowiedzi:', proximityResult);
        }

        // Check if user is within range
        if (!isWithinRange) {
          const distanceStr = distance !== undefined && distance !== null
            ? distance.toFixed(0) 
            : 'nieznana';
          const radiusStr = radius !== undefined && radius !== null
            ? radius.toFixed(0)
            : 'nieznany';
          const errorMessage = `Dystans: ${distanceStr} m (wymagany: ${radiusStr} m)`;
          console.warn('[PropertyDetailSheet] Użytkownik poza zasięgiem:', {
            distance: `${distance} metrów`,
            required: `${radius} metrów`,
          });
          toast.error(errorMessage);
          setIsVerifyingLocation(false);
          return;
        }
      } else {
        if (!needsGps) {
          console.log('[PropertyDetailSheet] GPS nie jest wymagany - pomijam weryfikację dystansu');
        } else {
          console.log('[PropertyDetailSheet] GPS bypass aktywny - pomijam weryfikację dystansu');
        }
      }

      // Step 3: Get current user and task_id
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        toast.error(t('errors.noUser'));
        setIsVerifyingLocation(false);
        return;
      }

      // Check-in to sesja na budynku: nie cofamy ukończonych zadań i nie wymagamy statusu in_progress.
      console.log('[PropertyDetailSheet] Pobieranie zadania sesji dla lokalizacji:', {
        location_id: property.id,
        org_id: currentOrgId,
        user_id: user.id,
        user_email: user.email,
      });

      let tasks: WorkSessionTask | null;
      try {
        tasks = await findLocationTaskForWorkSession(property.id, currentOrgId);
      } catch (tasksError) {
        console.error('[PropertyDetailSheet] BŁĄD pobierania zadania - Szczegóły:', tasksError);
        toast.error(t('errors.unexpectedError'));
        setIsVerifyingLocation(false);
        return;
      }

      if (!tasks) {
        console.warn('[PropertyDetailSheet] Nie znaleziono zadania dla lokalizacji:', {
          location_id: property.id,
          org_id: currentOrgId,
          user_id: user.id,
          user_email: user.email,
          message: 'Brak zadań dla tej lokalizacji',
        });
        toast.error('Brak zadań dla tej lokalizacji');
        setIsVerifyingLocation(false);
        return;
      }

      // Verify task belongs to correct org (nie sprawdzamy assigned_staff_id - zadania nie są przypisane do osób)
      if (tasks.org_id !== currentOrgId) {
        console.error('[PropertyDetailSheet] BŁĄD - Zadanie należy do innej organizacji:', {
          taskOrgId: tasks.org_id,
          currentOrgId,
          taskId: tasks.id,
        });
        toast.error('Błąd: Zadanie należy do innej organizacji');
        setIsVerifyingLocation(false);
        return;
      }

      const taskId = tasks.id;
      const taskStatus = tasks.status;
      const shouldUpdateStatus = false;
      let updateData: {
        check_in_metadata?: {
          latitude: number | null;
          longitude: number | null;
          timestamp: string;
          started_by?: string;
          resumed_by?: string;
        };
      } = {};

      if (shouldWriteCheckInMetadata(taskStatus)) {
        console.log('[PropertyDetailSheet] Rejestrujemy Check-in bez zmiany statusu zadania', { taskId, taskStatus });
        updateData = {
          check_in_metadata: {
            latitude: userLatitude,
            longitude: userLongitude,
            timestamp: new Date().toISOString(),
            ...(taskStatus === 'in_progress' ? { resumed_by: user.id } : { started_by: user.id }),
          },
        };
      } else {
        console.log('[PropertyDetailSheet] Check-in bez zmiany ukończonego zadania:', { taskId, taskStatus });
      }

      // Step 4: Update cleaning_tasks status and started_at (tylko jeśli potrzebne)

      // Aktualizuj tylko jeśli potrzebne (nie dla in_progress bez zmiany statusu)
      if (shouldUpdateStatus || Object.keys(updateData).length > 0) {
        console.log('[PropertyDetailSheet] Aktualizacja zadania:', {
          taskId,
          org_id: currentOrgId,
          user_id: user.id,
          location_id: property.id,
          currentStatus: taskStatus,
          shouldUpdateStatus,
          updateData,
        });

        const { data: updateResult, error: updateError } = await supabase
          .from('cleaning_tasks')
          .update(updateData)
          .eq('id', taskId)
          .eq('org_id', currentOrgId)
          .select(); // Select to verify update was successful
      
        if (updateError) {
          // CRITICAL: Szczegółowe logowanie błędu Supabase
          console.error('[PropertyDetailSheet] BŁĄD AKTUALIZACJI ZADANIA - Szczegóły:', {
            error: updateError,
            errorCode: updateError.code,
            errorMessage: updateError.message,
            errorDetails: updateError.details,
            errorHint: updateError.hint,
            taskId,
            org_id: currentOrgId,
            user_id: user.id,
            location_id: property.id,
            updateData,
          });
          
          // Sprawdź czy to błąd RLS (Row Level Security)
          if (updateError.code === '42501' || updateError.message?.includes('permission') || updateError.message?.includes('policy')) {
            console.error('[PropertyDetailSheet] BŁĄD RLS - Użytkownik nie ma uprawnień do aktualizacji tego zadania');
            toast.error('Brak uprawnień do aktualizacji zadania. Sprawdź ustawienia bezpieczeństwa w bazie danych.');
          } else {
            toast.error(`Błąd zapisu: ${updateError.message || t('errors.unexpectedError')}`);
          }
          
          // CRITICAL: NIE resetuj stanów walidacji przy błędzie - użytkownik nie powinien musieć wszystko robić od nowa
          setIsVerifyingLocation(false);
          return;
        }

        // Verify update was successful (tylko jeśli faktycznie aktualizowaliśmy)
        if (shouldUpdateStatus && (!updateResult || updateResult.length === 0)) {
          console.error('[PropertyDetailSheet] BŁĄD - Aktualizacja nie zwróciła danych:', {
            taskId,
            org_id: currentOrgId,
            updateResult,
          });
          toast.error('Błąd: Aktualizacja nie została potwierdzona przez serwer');
          setIsVerifyingLocation(false);
          return;
        }

        if (shouldUpdateStatus) {
          console.log('[PropertyDetailSheet] Zadanie zaktualizowane pomyślnie:', {
            taskId,
            org_id: currentOrgId,
            updatedTask: updateResult?.[0],
          });
        } else {
          console.log('[PropertyDetailSheet] Metadata zaktualizowane (status bez zmian):', {
            taskId,
            org_id: currentOrgId,
          });
        }
      } else {
        // Nie aktualizujemy - zadanie jest już in_progress, po prostu wpuszczamy użytkownika
        console.log('[PropertyDetailSheet] Zadanie już in_progress - pomijamy aktualizację, wpuszczamy użytkownika');
      }

      // Step 5: Log CHECK_IN action
      const { error: logError } = await supabase
        .from('task_execution_logs')
        .insert({
          task_id: taskId,
          user_id: user.id,
          action_type: 'CHECK_IN',
          notes: `GPS: ${userLatitude}, ${userLongitude}`,
        });

      if (logError) {
        console.error('[PropertyDetailSheet] Błąd zapisu logu:', logError);
        // Nie przerywamy procesu, jeśli log się nie udał
      }

      try {
        await startWorkSession({
          orgId: currentOrgId,
          locationId: property.id,
          userId: user.id,
          taskId,
          latitude: userLatitude,
          longitude: userLongitude,
        });
      } catch (sessionError) {
        console.error('[PropertyDetailSheet] Błąd zapisu sesji czasu pracy:', sessionError);
      }

      // Success - unlock UI
      setIsVerifyingLocation(false);
      setIsWorkStarted(true);
      setActiveWorkSession(property.id, true);
      void queryClient.invalidateQueries({ queryKey: [CLEANER_TASKS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [CLEANER_SECTIONS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [CLEANER_PROPERTIES_QUERY_KEY] });
      
      // Reset validation states after successful start (will be required again for checkout)
      setIsQRCodeValidated(false);
      setIsBiometricValidated(false);
      setShowValidationList(false);
      
      toast.success(t('property.workStarted'));
      
      // KROK KLUCZOWY: Odśwież dane bieżącej nieruchomości aby odblokować moduły
      // Wywołaj funkcję onWorkStarted() aby kłódki przy sekcjach zniknęły bez wychodzenia z widoku
      // To wymusza odświeżenie danych w CleanerDashboard (fetchProperties) i aktualizację selectedProperty
      if (onWorkStarted) {
        console.log('[PropertyDetailSheet] Wywołuję onWorkStarted() aby odświeżyć dane po starcie pracy');
        onWorkStarted(); // To wywołuje fetchProperties() w CleanerDashboard, które odświeża dane
      } else {
        console.warn('[PropertyDetailSheet] onWorkStarted callback nie jest dostępny - dane nie zostaną odświeżone');
      }
    } catch (error) {
      // CRITICAL: Szczegółowe logowanie wszystkich błędów
      console.error('[PropertyDetailSheet] BŁĄD podczas rozpoczynania pracy - Pełne szczegóły:', {
        error,
        errorType: typeof error,
        errorConstructor: error?.constructor?.name,
        errorString: String(error),
        errorJSON: JSON.stringify(error, Object.getOwnPropertyNames(error)),
      });
      
      // Jeśli to błąd Supabase, loguj szczegóły
      if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
        const supabaseError = error as { code?: string; message?: string; details?: string; hint?: string };
        console.error('[PropertyDetailSheet] BŁĄD SUPABASE:', {
          code: supabaseError.code,
          message: supabaseError.message,
          details: supabaseError.details,
          hint: supabaseError.hint,
        });
      }
      
      let errorMessage = t('errors.unexpectedError');
      if (error instanceof Error) {
        console.error('[PropertyDetailSheet] Szczegóły błędu Error:', {
          message: error.message,
          name: error.name,
          stack: error.stack,
          cause: 'cause' in error ? error.cause : undefined,
        });
        
        if (error.message === 'PERMISSION_DENIED') {
          errorMessage = t('errors.gpsPermissionDenied');
        } else if (error.message === 'POSITION_UNAVAILABLE') {
          errorMessage = t('errors.gpsNotAvailable');
        } else if (error.message === 'TIMEOUT') {
          errorMessage = t('errors.gpsTimeout');
        } else if (error.message === 'Geolocation not supported') {
          errorMessage = t('errors.gpsNotAvailable');
        } else {
          // Log unexpected errors with full details
          console.error('[PropertyDetailSheet] Nieoczekiwany błąd:', error);
          errorMessage = `${t('errors.unexpectedError')}: ${error.message}`;
        }
      } else {
        console.error('[PropertyDetailSheet] Nieznany błąd (nie jest Error):', {
          error,
          type: typeof error,
          stringified: String(error),
        });
      }
      
      toast.error(errorMessage);
      
      // CRITICAL: NIE resetuj stanów walidacji przy błędzie - użytkownik nie powinien musieć wszystko robić od nowa
      // Tylko zresetuj flagę ładowania
      setIsVerifyingLocation(false);
      
      // NIE resetuj:
      // - setIsQRCodeValidated(false) - zachowaj stan QR
      // - setIsBiometricValidated(false) - zachowaj stan biometrii
      // - setGpsStatus(null) - zachowaj stan GPS
    }
  };

  const handleRefreshGPS = async () => {
    if (!property || !currentOrgId) {
      toast.error(t('errors.unexpectedError'));
      return;
    }

    setIsRefreshingGPS(true);

    try {
      // Check if running on localhost (development mode - skip GPS verification)
      const isDevelopment = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname === '';

      if (isDevelopment) {
        console.log('[PropertyDetailSheet] Tryb deweloperski (localhost) - pomijanie weryfikacji GPS');
        toast.info('Tryb deweloperski - pomijanie weryfikacji GPS');
        setIsRefreshingGPS(false);
        return;
      }

      // Step 1: Get GPS location using navigator.geolocation.getCurrentPosition
      let userLatitude: number | null = null;
      let userLongitude: number | null = null;
      let position: GeolocationPosition | null = null;

      position = await new Promise<GeolocationPosition>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported'));
          return;
        }

        navigator.geolocation.getCurrentPosition(
          resolve,
          (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              reject(new Error('PERMISSION_DENIED'));
            } else if (error.code === error.POSITION_UNAVAILABLE) {
              reject(new Error('POSITION_UNAVAILABLE'));
            } else if (error.code === error.TIMEOUT) {
              reject(new Error('TIMEOUT'));
            } else {
              reject(error);
            }
          },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            }
        );
      });

      userLatitude = position.coords.latitude;
      userLongitude = position.coords.longitude;

      console.log('[PropertyDetailSheet] GPS Refresh - Współrzędne użytkownika:', {
        latitude: userLatitude,
        longitude: userLongitude,
        accuracy: position.coords.accuracy,
      });
      console.log('[PropertyDetailSheet] GPS debug - Współrzędne pracownika:', { latitude: userLatitude, longitude: userLongitude }, 'Współrzędne budynku (property.latitude/longitude):', property.latitude != null && property.longitude != null ? { latitude: property.latitude, longitude: property.longitude } : 'brak');

      // Step 2: Validate location proximity via RPC (RPC używa latitude/longitude z cleaning_locations)
      const { data: proximityData, error: proximityError } = await supabase.rpc(
        'check_location_proximity',
        {
          p_location_id: property.id,
          p_user_latitude: position.coords.latitude,
          p_user_longitude: position.coords.longitude,
        }
      );

      if (proximityError) {
        console.error('[PropertyDetailSheet] Błąd walidacji GPS:', proximityError);
        toast.error(t('errors.unexpectedError'));
        setIsRefreshingGPS(false);
        return;
      }

      // Log RAW response from API before parsing (critical for debugging)
      console.log('[PropertyDetailSheet] handleRefreshGPS - RAW proximityData from API:', proximityData);
      console.log('[PropertyDetailSheet] handleRefreshGPS - proximityData type:', typeof proximityData);
      console.log('[PropertyDetailSheet] handleRefreshGPS - proximityData.distance:', isPlainObject(proximityData) ? proximityData.distance : undefined);
      console.log('[PropertyDetailSheet] handleRefreshGPS - proximityData.radius:', isPlainObject(proximityData) ? proximityData.radius : undefined);
      console.log('[PropertyDetailSheet] handleRefreshGPS - proximityData.is_within_range:', isPlainObject(proximityData) ? proximityData.is_within_range : undefined);

      // Parse JSON response from RPC function
      const proximityResult = proximityData as {
        success?: boolean;
        is_within_range?: boolean;
        distance?: number;
        radius?: number;
        distance_meters?: number;
        geofence_radius_meters?: number;
        has_geo_location?: boolean;
        error?: string;
        location_coordinates?: { latitude: number; longitude: number };
        user_coordinates?: { latitude: number; longitude: number };
      };

      // CRITICAL: Serwer zwraca { distance, radius }, nie { distance_meters, geofence_radius_meters }
      const distance = proximityResult.distance ?? proximityResult.distance_meters;
      const radius = proximityResult.radius ?? proximityResult.geofence_radius_meters;
      const isWithinRange = proximityResult.is_within_range ?? false;

      // Log target coordinates from database (RPC zwraca location_coordinates z latitude/longitude)
      const targetCoords = proximityResult.location_coordinates;
      console.info('[PropertyDetailSheet] handleRefreshGPS - Budynek z bazy (RPC):', targetCoords || 'BRAK DANYCH - latitude/longitude nie istnieją w bazie dla tego obiektu');

      // Log detailed GPS comparison for debugging
      console.info('[PropertyDetailSheet] GPS Refresh - Szczegółowe porównanie:', {
        'Współrzędne obiektu z bazy (latitude/longitude)': targetCoords || 'brak danych',
        'Współrzędne użytkownika (z telefonu)': {
          latitude: userLatitude,
          longitude: userLongitude,
          accuracy: `${position.coords.accuracy?.toFixed(1)}m`,
        },
        'Odległość': distance !== undefined && distance !== null
          ? `${distance.toFixed(2)} metrów`
          : 'brak danych',
        'Wymagany promień': radius !== undefined && radius !== null
          ? `${radius} metrów`
          : 'brak danych',
        'W zasięgu': isWithinRange ? 'TAK' : 'NIE',
      });

      // ZAUFAJ RPC: Jeśli przyszły dane, po prostu je pokaż - ZMUŚ Reacta do przerysowania
      // CRITICAL: Always update state if we have distance from API response
      if (proximityResult && typeof distance === 'number' && !isNaN(distance)) {
        // Zmuś Reacta do przerysowania - zawsze aktualizuj stan z danymi z API
        const newGpsStatus = {
          distance: distance,
          radius: typeof radius === 'number' ? radius : undefined,
          isWithinRange: isWithinRange,
          isTracking: true,
        };
        setGpsStatus(newGpsStatus);
        console.log('[PropertyDetailSheet] handleRefreshGPS - Zaktualizowano stan z danymi z API:', {
          distance: newGpsStatus.distance,
          radius: newGpsStatus.radius,
          isWithinRange: newGpsStatus.isWithinRange,
          rawResponse: proximityResult,
        });
      } else {
        console.warn('[PropertyDetailSheet] handleRefreshGPS - Brak poprawnych danych w odpowiedzi:', proximityResult);
      }

      // Check if user is within range
      if (!isWithinRange) {
        const distanceStr = distance !== undefined && distance !== null
          ? distance.toFixed(0)
          : 'nieznana';
        const radiusStr = radius !== undefined && radius !== null
          ? radius.toFixed(0)
          : 'nieznany';
        const errorMessage = `Dystans: ${distanceStr} m (wymagany: ${radiusStr} m)`;
        console.warn('[PropertyDetailSheet] Użytkownik poza zasięgiem:', {
          distance: `${distanceStr} metrów`,
          required: `${radiusStr} metrów`,
        });
        toast.error(errorMessage);
        setIsRefreshingGPS(false);
        // Keep gpsStatus updated so UI can show distance even when out of range
        return;
      }

      // Success - user is within range
      const distanceStr = distance !== undefined && distance !== null ? distance.toFixed(0) : 'nieznana';
      const radiusStr = radius !== undefined && radius !== null ? radius : 'nieznany';
      toast.success(`Lokalizacja zweryfikowana. Dystans: ${distanceStr} metrów (wymagany: ${radiusStr} metrów)`);
      setIsRefreshingGPS(false);
    } catch (error) {
      console.error('[PropertyDetailSheet] Błąd podczas odświeżania GPS:', error);
      
      let errorMessage = t('errors.unexpectedError');
      if (error instanceof Error) {
        console.error('[PropertyDetailSheet] Szczegóły błędu:', {
          message: error.message,
          name: error.name,
          stack: error.stack,
        });
        
        if (error.message === 'PERMISSION_DENIED') {
          errorMessage = t('errors.gpsPermissionDenied');
        } else if (error.message === 'POSITION_UNAVAILABLE') {
          errorMessage = t('errors.gpsNotAvailable');
        } else if (error.message === 'TIMEOUT') {
          errorMessage = t('errors.gpsTimeout');
        } else if (error.message === 'Geolocation not supported') {
          errorMessage = t('errors.gpsNotAvailable');
        } else {
          console.error('[PropertyDetailSheet] Nieoczekiwany błąd:', error);
          errorMessage = `${t('errors.unexpectedError')}: ${error.message}`;
        }
      } else {
        console.error('[PropertyDetailSheet] Nieznany błąd:', error);
      }
      
      toast.error(errorMessage);
      setIsRefreshingGPS(false);
    }
  };

  const handleBiometricAuth = async () => {
    // Check if WebAuthn is supported
    if (!window.PublicKeyCredential) {
      console.warn('[PropertyDetailSheet] WebAuthn nie jest obsługiwane na tym urządzeniu');
      toast.info('Biometria nieobsługiwana na tym urządzeniu');
      // Automatycznie zalicz krok jeśli biometria nie jest obsługiwana
      setIsBiometricValidated(true);
      return;
    }

    try {
      console.log('[PropertyDetailSheet] Rozpoczynam weryfikację biometryczną...');

      // Generate a random challenge (uproszczona wersja - w pełnej implementacji challenge pochodzi z serwera)
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      // Konfiguracja publicKey dla platform authenticator (wymuszenie weryfikacji użytkownika na urządzeniu)
      // Uproszczona wersja: używamy create() jako "dowód tożsamości" - wymusi sprawdzenie odcisku/twarzy
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: challenge,
          rp: {
            name: 'DOMIO Cleaning',
            id: window.location.hostname, // Relying Party ID
          },
          user: {
            id: new Uint8Array(16), // Random user ID dla uproszczonej wersji
            name: 'user@example.com', // Placeholder
            displayName: 'Użytkownik',
          },
          pubKeyCredParams: [
            { alg: -7, type: 'public-key' }, // ES256
            { alg: -257, type: 'public-key' }, // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform', // Wymusza użycie platform authenticator (FaceID/TouchID)
            userVerification: 'required', // WYMUSZA weryfikację użytkownika (FaceID/TouchID)
            requireResidentKey: false,
          },
          timeout: 60000, // 60 sekund timeout
          attestation: 'direct',
        },
      } as CredentialCreationOptions);

      // Sukces - użytkownik poprawnie zweryfikował się biometrycznie
      if (credential) {
        console.log('[PropertyDetailSheet] Weryfikacja biometryczna zakończona pomyślnie');
        setIsBiometricValidated(true);
        toast.success('Weryfikacja biometryczna zakończona pomyślnie', {
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('[PropertyDetailSheet] Błąd weryfikacji biometrycznej:', error);
      
      // Sprawdź czy użytkownik anulował operację
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.message.includes('canceled') || error.message.includes('user')) {
          toast.error('Weryfikacja biometryczna nie powiodła się lub została anulowana', {
            duration: 3000,
          });
        } else {
          toast.error(`Błąd weryfikacji: ${error.message}`, {
            duration: 3000,
          });
        }
      } else {
        toast.error('Weryfikacja biometryczna nie powiodła się lub została anulowana', {
          duration: 3000,
        });
      }
    }
  };

  const handleCheckIn = () => {
    handleStartWork();
  };

  const handleFinishWork = async () => {
    if (!property || !currentOrgId) {
      console.error('[PropertyDetailSheet] handleFinishWork - brak property lub currentOrgId:', { property: !!property, currentOrgId });
      toast.error(t('errors.unexpectedError'));
      return;
    }

    setShowIncompleteConfirm(false);
    setIsVerifyingLocation(true);

    try {
      // Step 1: Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        toast.error(t('errors.noUser'));
        setIsVerifyingLocation(false);
        return;
      }

      // Check-out kończy wizytę, nie wymaga zadania in_progress i nie oznacza niewykonanych zadań jako done.
      console.log('[PropertyDetailSheet] handleFinishWork - Weryfikacja lokalizacji GPS przed zakończeniem pracy');
      
      // Check if running on localhost or development mode
      const isDevelopment = process.env.NODE_ENV === 'development' ||
                           window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname === '';

      // Get GPS location using navigator.geolocation.getCurrentPosition
      // Skip GPS verification if bypass is enabled OR in development mode OR GPS is not required
      let userLatitude: number | null = null;
      let userLongitude: number | null = null;
      let position: GeolocationPosition | null = null;

      const shouldSkipGPS = isDevelopment || gpsBypassEnabled || !needsGps;

      if (shouldSkipGPS) {
        if (!needsGps) {
          console.log('[PropertyDetailSheet] handleFinishWork - GPS nie jest wymagany w konfiguracji - pomijanie weryfikacji GPS');
        } else {
          console.log('[PropertyDetailSheet] handleFinishWork - Tryb deweloperski lub bypass GPS - pomijanie weryfikacji GPS');
        }
        // Use mock coordinates for development/bypass or when GPS is not required
        userLatitude = 52.2297;
        userLongitude = 21.0122;
      } else {
        position = await new Promise<GeolocationPosition>((resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error('Geolocation not supported'));
            return;
          }

          navigator.geolocation.getCurrentPosition(
            resolve,
            (error) => {
              if (error.code === error.PERMISSION_DENIED) {
                reject(new Error('PERMISSION_DENIED'));
              } else if (error.code === error.POSITION_UNAVAILABLE) {
                reject(new Error('POSITION_UNAVAILABLE'));
              } else if (error.code === error.TIMEOUT) {
                reject(new Error('TIMEOUT'));
              } else {
                reject(error);
              }
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            }
          );
        });

        userLatitude = position.coords.latitude;
        userLongitude = position.coords.longitude;

        console.log('[PropertyDetailSheet] handleFinishWork - GPS - Współrzędne użytkownika:', {
          latitude: userLatitude,
          longitude: userLongitude,
          accuracy: position.coords.accuracy,
        });
      }

      // Validate location proximity via RPC (returns detailed JSON)
      // Skip RPC call if GPS bypass is enabled
      let proximityResult: {
        success?: boolean;
        is_within_range?: boolean;
        distance?: number;
        radius?: number;
        distance_meters?: number;
        geofence_radius_meters?: number;
        has_geo_location?: boolean;
        error?: string;
        location_coordinates?: { latitude: number; longitude: number };
        user_coordinates?: { latitude: number; longitude: number };
      } | null = null;

      if (!shouldSkipGPS) {
        console.log('[PropertyDetailSheet] handleFinishWork - ID Lokalizacji:', property.id);
        const empLat = position ? position.coords.latitude : userLatitude;
        const empLng = position ? position.coords.longitude : userLongitude;
        console.log('[PropertyDetailSheet] GPS debug - Współrzędne pracownika:', { latitude: empLat, longitude: empLng }, 'Współrzędne budynku (property.latitude/longitude):', property.latitude != null && property.longitude != null ? { latitude: property.latitude, longitude: property.longitude } : 'brak');
        if (position) {
          console.log('[PropertyDetailSheet] handleFinishWork - Wysyłam do bazy:', { lat: position.coords.latitude, lng: position.coords.longitude });
        } else {
          console.log('[PropertyDetailSheet] handleFinishWork - Wysyłam do bazy:', { lat: userLatitude, lng: userLongitude });
        }
        
        const { data: proximityData, error: proximityError } = await supabase.rpc(
          'check_location_proximity',
          {
            p_location_id: property.id,
            p_user_latitude: empLat,
            p_user_longitude: empLng,
          }
        );

        if (proximityError) {
          console.error('[PropertyDetailSheet] handleFinishWork - Błąd walidacji GPS:', proximityError);
          toast.error(t('errors.unexpectedError'));
          setIsVerifyingLocation(false);
          return;
        }

        // Log RAW response from API before parsing (critical for debugging)
        console.log('[PropertyDetailSheet] handleFinishWork - RAW proximityData from API:', proximityData);
        console.log('[PropertyDetailSheet] handleFinishWork - proximityData type:', typeof proximityData);
        console.log('[PropertyDetailSheet] handleFinishWork - proximityData.distance:', isPlainObject(proximityData) ? proximityData.distance : undefined);
        console.log('[PropertyDetailSheet] handleFinishWork - proximityData.radius:', isPlainObject(proximityData) ? proximityData.radius : undefined);
        console.log('[PropertyDetailSheet] handleFinishWork - proximityData.is_within_range:', isPlainObject(proximityData) ? proximityData.is_within_range : undefined);

        // Parse JSON response from RPC function
        proximityResult = proximityData as typeof proximityResult;

        // CRITICAL: Serwer zwraca { distance, radius }, nie { distance_meters, geofence_radius_meters }
        const distance = proximityResult?.distance ?? proximityResult?.distance_meters;
        const radius = proximityResult?.radius ?? proximityResult?.geofence_radius_meters;
        const isWithinRange = proximityResult?.is_within_range ?? false;

        // Log target coordinates from database (RPC zwraca location_coordinates z latitude/longitude)
        const targetCoords = proximityResult?.location_coordinates;
        console.info('[PropertyDetailSheet] handleFinishWork - Budynek z bazy (RPC):', targetCoords || 'BRAK DANYCH - latitude/longitude nie istnieją w bazie dla tego obiektu');

        // Detailed GPS diagnostics logging
        console.log('[PropertyDetailSheet] handleFinishWork - GPS - Szczegółowa diagnostyka:', {
          'Współrzędne obiektu z bazy (latitude/longitude)': targetCoords || 'brak danych',
          'Współrzędne użytkownika': proximityResult?.user_coordinates || {
            latitude: userLatitude,
            longitude: userLongitude,
          },
          'Odległość': distance !== undefined && distance !== null
            ? `${distance.toFixed(2)} metrów`
            : 'brak danych',
          'Wymagany promień': radius !== undefined && radius !== null
            ? `${radius} metrów`
            : 'brak danych',
          'W zasięgu': isWithinRange ? 'TAK' : 'NIE',
          'latitude/longitude w bazie': proximityResult?.has_geo_location ? 'TAK' : 'NIE',
          'Sukces': proximityResult?.success ? 'TAK' : 'NIE',
        });

        // ZAUFAJ RPC: Jeśli przyszły dane, po prostu je pokaż - ZMUŚ Reacta do przerysowania
        // CRITICAL: Always update state if we have distance from API response
        if (proximityResult && typeof distance === 'number' && !isNaN(distance)) {
          // Zmuś Reacta do przerysowania - zawsze aktualizuj stan z danymi z API
          const newGpsStatus = {
            distance: distance,
            radius: typeof radius === 'number' ? radius : undefined,
            isWithinRange: isWithinRange,
            isTracking: true,
          };
          setGpsStatus(newGpsStatus);
          console.log('[PropertyDetailSheet] handleFinishWork - Zaktualizowano stan z danymi z API:', {
            distance: newGpsStatus.distance,
            radius: newGpsStatus.radius,
            isWithinRange: newGpsStatus.isWithinRange,
            rawResponse: proximityResult,
          });
        } else {
          console.warn('[PropertyDetailSheet] handleFinishWork - Brak poprawnych danych w odpowiedzi:', proximityResult);
        }

        // Check if user is within range - BLOCK completion if outside range
        if (!isWithinRange) {
          const distanceStr = distance !== undefined && distance !== null
            ? distance.toFixed(0) 
            : 'nieznana';
          const errorMessage = `Błąd: Jesteś poza zasięgiem obiektu. Nie możesz zakończyć pracy (Dystans: ${distanceStr} m)`;
          console.warn('[PropertyDetailSheet] handleFinishWork - Użytkownik poza zasięgiem:', {
            distance: `${distance} metrów`,
            required: `${radius} metrów`,
          });
          toast.error(errorMessage);
          setIsVerifyingLocation(false);
          return;
        }
      } else {
        if (!needsGps) {
          console.log('[PropertyDetailSheet] handleFinishWork - GPS nie jest wymagany - pomijam weryfikację dystansu');
        } else {
          console.log('[PropertyDetailSheet] handleFinishWork - GPS bypass aktywny - pomijam weryfikację dystansu');
        }
      }

      const remaining = countRemainingPropertyTasks(property.sections);
      let sessionTask = null;
      try {
        sessionTask = await findCheckoutTaskForLocation({
          locationId: property.id,
          orgId: currentOrgId,
          userId: user.id,
        });
      } catch (sessionError) {
        console.error('[PropertyDetailSheet] handleFinishWork - Błąd pobierania zadania sesji:', sessionError);
      }

      if (sessionTask) {
        const { error: logError } = await supabase
          .from('task_execution_logs')
          .insert({
            task_id: sessionTask.id,
            user_id: user.id,
            action_type: 'CHECK_OUT',
            notes: formatCheckOutNotes({
              latitude: userLatitude,
              longitude: userLongitude,
              remainingTasks: remaining,
            }),
          });

        if (logError) {
          console.error('[PropertyDetailSheet] handleFinishWork - Błąd zapisu logu:', logError);
        }
      } else {
        console.warn('[PropertyDetailSheet] handleFinishWork - Brak zadania do logu CHECK_OUT; kończymy lokalną sesję');
      }

      try {
        const openSessions = await fetchOpenWorkSessions(user.id, currentOrgId);
        const currentSession = openSessions.find((session) => session.location_id === property.id);
        if (currentSession) {
          await closeWorkSession({
            sessionId: currentSession.id,
            locationId: property.id,
            reason: 'manual',
            latitude: userLatitude,
            longitude: userLongitude,
            remainingTasks: remaining,
          });
        }
      } catch (sessionError) {
        console.error('[PropertyDetailSheet] handleFinishWork - Błąd zamknięcia sesji:', sessionError);
      }

      setIsVerifyingLocation(false);
      setIsWorkStarted(false);
      setActiveWorkSession(property.id, false);
      setIsQRCodeValidated(false);
      setIsBiometricValidated(false);
      setShowValidationList(false);

      void queryClient.invalidateQueries({ queryKey: [CLEANER_TASKS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [CLEANER_SECTIONS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [CLEANER_PROPERTIES_QUERY_KEY] });

      toast.success(remaining > 0 ? t('property.workEndedPartial') : t('property.workEnded'));
      
      // KROK KLUCZOWY: Odśwież dane aby UI zaktualizowało się natychmiast (przed zamknięciem)
      if (onWorkStarted) {
        console.log('[PropertyDetailSheet] handleFinishWork - Wywołuję onWorkStarted() aby odświeżyć dane po zakończeniu pracy');
        await Promise.resolve(onWorkStarted());
      } else {
        console.warn('[PropertyDetailSheet] handleFinishWork - onWorkStarted callback nie jest dostępny - dane nie zostaną odświeżone');
      }

      // Zamknij panel dopiero po odświeżeniu danych (naprawa "zombie session")
      closeView();
    } catch (error) {
      console.error('[PropertyDetailSheet] handleFinishWork - Błąd podczas kończenia pracy:', error);
      
      let errorMessage = t('errors.unexpectedError');
      if (error instanceof Error) {
        console.error('[PropertyDetailSheet] handleFinishWork - Szczegóły błędu:', {
          message: error.message,
          name: error.name,
          stack: error.stack,
        });
        
        if (error.message === 'PERMISSION_DENIED') {
          errorMessage = t('errors.gpsPermissionDenied');
        } else if (error.message === 'POSITION_UNAVAILABLE') {
          errorMessage = t('errors.gpsNotAvailable');
        } else if (error.message === 'TIMEOUT') {
          errorMessage = t('errors.gpsTimeout');
        } else if (error.message === 'Geolocation not supported') {
          errorMessage = t('errors.gpsNotAvailable');
        } else {
          // Log unexpected errors with full details
          console.error('[PropertyDetailSheet] handleFinishWork - Nieoczekiwany błąd:', error);
          errorMessage = `${t('errors.unexpectedError')}: ${error.message}`;
        }
      } else {
        console.error('[PropertyDetailSheet] handleFinishWork - Nieznany błąd:', error);
      }
      
      toast.error(errorMessage);
      setIsVerifyingLocation(false);
    }
  };

  const handleCheckOut = () => {
    // Show validation list instead of finishing immediately
    setShowValidationList(true);
  };

  const remainingTasks = countRemainingPropertyTasks(property.sections);

  const handleConfirmFinishWork = async () => {
    if (!isAllRequirementsMet()) {
      toast.error('Wypełnij wszystkie wymagania przed zakończeniem pracy');
      return;
    }
    if (remainingTasks > 0) {
      setShowIncompleteConfirm(true);
      return;
    }
    await handleFinishWork();
  };

  // Funkcja bezpiecznego anulowania skanowania QR
  const handleCancelScan = async () => {
    console.log('[PropertyDetailSheet] Anulowanie skanowania QR...');
    
    // Zatrzymaj skaner jeśli działa
    if (qrCodeScannerRef.current) {
      try {
        await qrCodeScannerRef.current.stop();
        qrCodeScannerRef.current.clear();
      } catch (err) {
        console.error('[PropertyDetailSheet] Błąd zatrzymania skanera przy anulowaniu:', err);
      }
      qrCodeScannerRef.current = null;
    }
    
    // Resetuj stan skanowania - to spowoduje usunięcie kontenera z DOM
    setIsScanning(false);
    setScannedCode(null);
    setQrScannerError(null);
    // NIE resetuj isQRCodeValidated - jeśli kod był już zweryfikowany, status powinien pozostać
  };

  const handleSectionClick = (section: Section) => {
    console.log(`[PropertyDetailSheet] Kliknięto sekcję: ID=${section.id}, Nazwa=${section.name}`);
    if (!property?.id) return;
    navigate(`/property/${property.id}/section/${section.id}?tab=zadania`);
  };

  const handlePropertyTabChange = (value: string) => {
    if (value !== 'work' && value !== 'instructions' && value !== 'warehouse') return;
    const tab = value as PropertyDetailTab;
    const next = new URLSearchParams(searchParams);
    next.set('tab', propertyDetailTabToSearchParam(tab));
    setSearchParams(next, { replace: false });
  };

  const handleTranslateSop = async () => {
    if (sopTranslatedText) {
      setSopTranslatedText(null);
      return;
    }
    const instr = property?.sections?.[0]?.instructionNotes;
    const code = property?.sections?.[0]?.accessCode;
    const parts: string[] = [];
    if (code) parts.push(`Kod dostępu: ${code}`);
    if (instr) parts.push(instr);
    const textToTranslate = parts.join('\n\n');
    if (!textToTranslate) return;
    setIsSopTranslating(true);
    try {
      const translated = await translateText(textToTranslate);
      setSopTranslatedText(translated);
    } catch (error) {
      toast.error(t('translation.error'));
      console.error('[PropertyDetailSheet] SOP translate error:', error);
    } finally {
      setIsSopTranslating(false);
    }
  };

  // Bezpieczeństwo danych: sprawdzenie czy property istnieje przed renderowaniem
  if (!property) {
    return null;
  }

  return (
    <div className={embedded ? (embeddedClassName || "min-h-screen bg-canvas flex flex-col") : "w-full sm:max-w-lg overflow-y-auto border-none shadow-xl"}>
      <div className={embedded ? "w-full max-w-3xl mx-auto p-4 sm:p-6" : ""}>
          <header className="pb-4">
            <div className="work-page-header">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col items-start w-full">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mb-3 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 px-3 py-2 rounded-md transition-colors"
                  onClick={handleSafeExit}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  {t('common.back')}
                </Button>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  {property.address}
                </h1>
                <p className="mt-1 text-sm font-medium text-slate-600 dark:text-slate-300">
                  {property.clientName}
                </p>
              </div>
              <Badge variant="secondary" className={cn("font-medium", priority.className)}>
                {priority.label}
              </Badge>
            </div>
            </div>
          </header>

          {/* Validation Loading State */}
          {isValidating && validationType && (
            <ValidationLoader 
              title={validationType === 'checkin' ? t('property.startingWork') : t('property.finishingTask')}
              steps={validationType === 'checkin' 
                ? getCheckInSteps(validationStep) 
                : getCheckOutSteps(validationStep)
              }
            />
          )}

          {/* Main Content */}
          {!isValidating && (
            <div className="space-y-4 pb-6">
              <Tabs value={propertyTab} onValueChange={handlePropertyTabChange} className="w-full">
                <TabsList className="work-tabs grid w-full grid-cols-3 gap-1 h-auto">
                  <TabsTrigger value="work" className="work-tab">Zadania</TabsTrigger>
                  <TabsTrigger value="instructions" className="work-tab">Instrukcje</TabsTrigger>
                  <TabsTrigger value="warehouse" className="work-tab">Magazyn</TabsTrigger>
                </TabsList>
                <TabsContent
                  value="work"
                  forceMount
                  className="mt-4 space-y-4 outline-none data-[state=inactive]:hidden"
                >
              {/* Action Buttons */}
              <div className="space-y-2">
                {isScanning && needsQr ? (
                  /* QR Scanner Active - pokazuj tylko jeśli QR jest wymagany */
                  <div className="rounded-xl border-2 border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-4">
                    <div className="flex flex-col items-center gap-4">
                      {/* Przycisk Anuluj - na górze, zawsze widoczny */}
                      <div className="w-full flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
                          onClick={handleCancelScan}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Anuluj
                        </Button>
                      </div>
                      
                      <div className="w-full">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 text-center">
                          Skanuj kod QR nieruchomości
                        </h3>
                        
                        {/* Kontener dla skanera QR - renderowany tylko gdy isScanning === true (cały blok jest już warunkowy) */}
                        <div 
                          id={`qr-reader-${property.id}`}
                          ref={qrCodeScannerContainerRef}
                          className="w-full rounded-lg overflow-hidden bg-black"
                          style={{ minHeight: '300px' }}
                        />

                        {/* Komunikat o błędzie skanera */}
                        {qrScannerError && (
                          <div className="mt-3 p-3 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                            <p className="text-sm text-red-700 dark:text-red-400">
                              ⚠️ {qrScannerError}
                            </p>
                          </div>
                        )}

                        {/* Komunikat o odczytanym kodzie */}
                        {scannedCode && !isQRCodeValidated && (
                          <div className="mt-3 p-3 rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800">
                            <p className="text-sm text-amber-700 dark:text-amber-400">
                              Ostatni odczytany kod: {scannedCode}
                            </p>
                          </div>
                        )}

                        {/* Komunikat o poprawnym kodzie */}
                        {isQRCodeValidated && (
                          <div className="mt-3 p-3 rounded-md bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800">
                            <p className="text-sm text-green-700 dark:text-green-400 font-semibold">
                              ✓ Kod poprawny! Weryfikacja zakończona pomyślnie.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Dynamic Validation Requirements Checklist - pokazuje tylko wymagane metody */}
                    {/* Dla check-in: zawsze pokazuj */}
                    {/* Dla check-out: pokazuj tylko gdy showValidationList === true */}
                    {((!isWorkStarted || showValidationList) && (needsGps || needsQr || needsBiometric)) && (
                      <div className="flex flex-col gap-2 px-3 py-2 rounded-md bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-sm shadow-sm">
                        <span className="font-semibold text-slate-800 dark:text-slate-200 mb-1">Wymagania walidacji:</span>
                        <div className="flex flex-col gap-2">
                          {needsGps && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                              <span className="text-slate-700 dark:text-slate-300">Lokalizacja GPS</span>
                              <span className="ml-auto">{isGpsValid ? '✅' : '⏳'}</span>
                            </div>
                          )}
                          {needsQr && (
                            <div className="flex items-center gap-2">
                              <QrCode className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                              <span className="text-slate-700 dark:text-slate-300">Kod QR</span>
                              <span className="ml-auto">{isQrValid ? '✅' : '⏳'}</span>
                            </div>
                          )}
                          {needsBiometric && (
                            <div className="flex items-center gap-2">
                              <Fingerprint className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                              <span className="text-slate-700 dark:text-slate-300">Biometria</span>
                              <span className="ml-auto">{isBiometricValid ? '✅' : '⏳'}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* QR Code Validation Status - Wyświetl nad przyciskiem gdy zweryfikowany (tylko jeśli QR jest wymagany) */}
                    {!isWorkStarted && needsQr && isQRCodeValidated && (
                      <div className="px-3 py-2 rounded-md bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm font-medium">Kod QR zweryfikowany</span>
                      </div>
                    )}
                    
                    <div className="flex gap-3">
                      {!isWorkStarted ? (
                        <>
                          <Button 
                            className={cn(
                              "flex-1 h-12 text-base font-medium",
                              isAllRequirementsMet() ? "bg-green-600 hover:bg-green-700 text-white" : ""
                            )}
                            onClick={handleCheckIn}
                            disabled={
                              isVerifyingLocation || 
                              isRefreshingGPS || 
                              !isAllRequirementsMet()
                            }
                          >
                          {isVerifyingLocation ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              {t('common.loading')}
                            </>
                          ) : (
                            <>
                              <Play className="mr-2 h-5 w-5" />
                              {t('buttons.startWork')}
                            </>
                          )}
                        </Button>
                        {needsGps && (
                          <Button 
                            variant="outline"
                            className="h-12 px-4"
                            onClick={handleRefreshGPS}
                            disabled={isVerifyingLocation || isRefreshingGPS}
                            title={t('buttons.refreshGPS')}
                          >
                            {isRefreshingGPS ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                              <Navigation className="h-5 w-5" />
                            )}
                          </Button>
                        )}
                        {needsQr && (
                          <Button 
                            variant="outline"
                            className="h-12 px-4"
                            onClick={() => setIsScanning(true)}
                            disabled={isVerifyingLocation || isRefreshingGPS}
                            title="Skanuj kod QR"
                          >
                            <QrCode className="h-5 w-5" />
                          </Button>
                        )}
                        {needsBiometric && (
                          <Button 
                            variant="outline"
                            className="h-12 px-4"
                            onClick={handleBiometricAuth}
                            disabled={isVerifyingLocation || isRefreshingGPS}
                            title="Potwierdź tożsamość"
                          >
                            <Fingerprint className="h-5 w-5" />
                          </Button>
                        )}
                        {/* Debug: GPS Bypass Button - Only visible in development mode or localhost */}
                        {(process.env.NODE_ENV === 'development' || 
                          window.location.hostname === 'localhost' || 
                          window.location.hostname === '127.0.0.1') && (
                          <Button 
                            variant={gpsBypassEnabled ? 'default' : 'outline'}
                            className="h-12 px-4"
                            onClick={() => setGpsBypassEnabled(!gpsBypassEnabled)}
                            title="Debug: Pomiń GPS"
                          >
                            {gpsBypassEnabled ? '✓' : '⚡'}
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Gdy praca trwa: pokaż tylko przycisk "Zakończ zadanie" (domyślnie) */}
                        {!showValidationList ? (
                          <Button 
                            className="w-full h-12 text-base font-medium bg-green-600 hover:bg-green-700 text-white"
                            onClick={handleCheckOut}
                            disabled={isVerifyingLocation}
                          >
                            {isVerifyingLocation ? (
                              <>
                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                {t('common.loading')}
                              </>
                            ) : (
                              <>
                                <CheckCircle2 className="mr-2 h-5 w-5" />
                                Zakończ
                              </>
                            )}
                          </Button>
                        ) : (
                          /* Gdy showValidationList === true: pokaż checklistę i przyciski walidacji */
                          <div className="flex flex-col gap-3 w-full">
                            {/* Finalny przycisk potwierdzający - na górze, najbardziej wyeksponowany */}
                            <Button 
                              className={cn(
                                "w-full h-16 text-lg font-semibold",
                                isAllRequirementsMet() ? "bg-green-600 hover:bg-green-700 text-white" : ""
                              )}
                              onClick={handleConfirmFinishWork}
                              disabled={isVerifyingLocation || !isAllRequirementsMet()}
                            >
                              {isVerifyingLocation ? (
                                <>
                                  <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                                  {t('common.loading')}
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 className="mr-2 h-6 w-6" />
                                  Zakończ
                                </>
                              )}
                            </Button>
                            {/* Przyciski pomocnicze - poniżej przycisku zakończenia, duże i na pełną szerokość */}
                            <div className="flex flex-col gap-2 w-full">
                              {needsGps && (
                                <Button 
                                  variant="outline"
                                  className="w-full h-16 text-base font-medium"
                                  onClick={handleRefreshGPS}
                                  disabled={isVerifyingLocation || isRefreshingGPS}
                                  title={t('buttons.refreshGPS')}
                                >
                                  {isRefreshingGPS ? (
                                    <>
                                      <Loader2 className="mr-2 h-8 w-8 animate-spin" />
                                      Odświeżanie GPS...
                                    </>
                                  ) : (
                                    <>
                                      <Navigation className="mr-2 h-8 w-8" />
                                      Odśwież GPS
                                    </>
                                  )}
                                </Button>
                              )}
                              {needsQr && (
                                <Button 
                                  variant="outline"
                                  className="w-full h-16 text-base font-medium"
                                  onClick={() => setIsScanning(true)}
                                  disabled={isVerifyingLocation || isRefreshingGPS}
                                  title="Skanuj kod QR"
                                >
                                  <QrCode className="mr-2 h-8 w-8" />
                                  Skanuj kod QR
                                </Button>
                              )}
                              {needsBiometric && (
                                <Button 
                                  variant="outline"
                                  className="w-full h-16 text-base font-medium"
                                  onClick={handleBiometricAuth}
                                  disabled={isVerifyingLocation || isRefreshingGPS}
                                  title="Potwierdź tożsamość"
                                >
                                  <Fingerprint className="mr-2 h-8 w-8" />
                                  Potwierdź tożsamość
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                )}
                {/* GPS Status Feedback - pokazuj tylko jeśli GPS jest wymagany i nie jesteśmy w trybie zakończenia pracy */}
                {(!isWorkStarted || showValidationList) && needsGps && gpsStatus && (
                  <div className={cn(
                    "text-sm px-3 py-2 rounded-md transition-colors",
                    gpsStatus.isWithinRange 
                      ? "bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800"
                      : "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                  )}>
                    {gpsStatus.isWithinRange ? (
                      <span className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        Jesteś na miejscu ({gpsStatus.distance !== undefined && typeof gpsStatus.distance === 'number' ? gpsStatus.distance.toFixed(0) : '?'} m). Możesz zacząć.
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Jesteś za daleko: {gpsStatus.distance !== undefined && typeof gpsStatus.distance === 'number' ? gpsStatus.distance.toFixed(0) : '?'} m. Podejdź bliżej (wymagane: {gpsStatus.radius !== undefined && typeof gpsStatus.radius === 'number' ? gpsStatus.radius : '?'} m).
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Preview Banner */}
              {!isWorkStarted && <PreviewBanner />}

              {/* Prace Dodatkowe / Odśnieżanie – widoczne tylko gdy włączone w ustawieniach organizacji */}
              {isSnowSeason && (
                <div className="space-y-4">
                  <SnowSessionSection
                    locationId={property.id}
                    orgId={currentOrgId}
                    latitude={property.latitude ?? undefined}
                    longitude={property.longitude ?? undefined}
                    onSessionChange={() => {
                      setTimeout(() => findTaskId(), 200);
                      onWorkStarted?.();
                    }}
                  />
                </div>
              )}

              {/* Sections List */}
              <div className="space-y-4">
                <h3 className="work-section-label">
                  {t('section.objectSections')}
                </h3>
                <div className="space-y-4">
                  {property.sections.map((section) => (
                    <SectionCard
                      key={section.id}
                      section={section}
                      isWorkStarted={isWorkStarted}
                      onClick={() => handleSectionClick(section)}
                    />
                  ))}
                </div>
              </div>

              {currentOrgId && (
                <ExtraPaidSessionSection
                  locationId={property.id}
                  orgId={currentOrgId}
                  qrCodeToken={property.qr_code_token}
                  locationMasterId={property.location_master_id}
                />
              )}
                </TabsContent>

                <TabsContent
                  value="instructions"
                  forceMount
                  className="mt-4 space-y-4 pt-2 outline-none data-[state=inactive]:hidden"
                >
                  <div className="space-y-4">
                    <div
                      className={cn(
                        "relative rounded-xl overflow-hidden transition-all border-2 bg-white shadow-md",
                        "dark:bg-zinc-900",
                        "border-emerald-400 dark:border-emerald-700"
                      )}
                    >
                      <div className="px-4 py-3 border-b-2 border-emerald-200 dark:border-emerald-800 flex items-center gap-3 bg-emerald-100 dark:bg-emerald-950/60">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/20 dark:bg-emerald-500/20">
                          <FileText className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
                        </div>
                        <div className="text-left flex-1">
                          <h3 className="font-bold text-emerald-950 dark:text-emerald-100">{t('sections.sop')}</h3>
                          <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">
                            {t('property.instructions')}
                          </p>
                        </div>
                      </div>
                      <div className="px-4 pb-4 pt-4">
                        <div className="flex items-center justify-end gap-2 mb-3">
                          {i18n.language === 'ua' && (property.sections[0]?.instructionNotes || property.sections[0]?.accessCode) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={handleTranslateSop}
                              disabled={isSopTranslating}
                              className="h-8 w-8 flex-shrink-0"
                              title={sopTranslatedText ? t('translation.hideTranslation') : t('translation.translate')}
                            >
                              {isSopTranslating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Languages className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                        <SOPSection
                          instructionNotes={property.sections[0]?.instructionNotes}
                          accessCode={property.sections[0]?.accessCode}
                        />
                        {sopTranslatedText && (
                          <div className="space-y-1 mt-3">
                            <p className="text-xs text-muted-foreground italic">
                              {t('translation.autoTranslation')}
                            </p>
                            <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-line border border-blue-200 dark:border-blue-800">
                              {sopTranslatedText}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent
                  value="warehouse"
                  forceMount
                  className="mt-4 space-y-4 pt-2 outline-none data-[state=inactive]:hidden"
                >
                  <div className="space-y-4">
                    <div
                      className={cn(
                        "relative rounded-xl overflow-hidden transition-all border-2 bg-white shadow-md",
                        "dark:bg-zinc-900",
                        "border-blue-400 dark:border-blue-800"
                      )}
                    >
                      <div className="px-4 py-3 border-b-2 border-blue-200 dark:border-blue-900 flex items-center gap-3 bg-blue-100 dark:bg-blue-950/60">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-200 dark:bg-blue-900/60">
                          <Package className="h-5 w-5 text-blue-700 dark:text-blue-400" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-bold text-blue-950 dark:text-slate-100">{t('section.objectInventory')}</h3>
                          <p className="text-xs font-medium text-blue-800 dark:text-blue-300">
                            {property.inventory.length} {t('section.products')}
                          </p>
                        </div>
                      </div>
                      <div className="px-4 pb-4 pt-4">
                        <InventoryOrderSection
                          locationId={property.id}
                          orgId={currentOrgId}
                          inventory={property.inventory}
                          disabled={false}
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
      </div>
      <AlertDialog open={showIncompleteConfirm} onOpenChange={setShowIncompleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('property.incompleteFinishTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('property.incompleteFinishDescription', { count: remainingTasks })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isVerifyingLocation}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={isVerifyingLocation}
              onClick={(event) => {
                event.preventDefault();
                void handleFinishWork();
              }}
            >
              {t('property.incompleteFinishConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
